import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, InputNumber, Button, Space, Typography, message } from 'antd';
import './DiagnosticForm.css';
import { SpecBadge } from './SpecBadge';
import { VerdictBar } from './VerdictBar';
import { diagnosticService } from '../../services/diagnosticService';
import type { Run, Procedure, CellResult, SpecUsed } from '../../services/diagnosticService';

const { Text } = Typography;
type Verdict = 'green' | 'yellow' | 'red';

export interface DiagnosticFormProps {
  run: Run;
  procedure: Procedure;
  onCompleted?: (run: Run) => void;
}

interface CellState {
  cell: string;
  value: number | null;
  verdict: Verdict | null;
}

/** Build initial cells from procedure config + run vehicle snapshot. */
function buildCells(proc: Procedure, run: Run): CellState[] {
  const schema = proc.inputs_schema;
  if (schema?.kind === 'per_cylinder') {
    const n = run.vehicle_snapshot?.cylinder_count ?? 4;
    return Array.from({ length: n }, (_, i) => ({ cell: `cyl${i + 1}`, value: null, verdict: null }));
  }
  if (schema?.kind === 'per_position') {
    return (schema.positions as string[]).map((p) => ({ cell: p, value: null, verdict: null }));
  }
  if (schema?.kind === 'single') {
    return [{ cell: schema.cell?.key ?? 'value', value: null, verdict: null }];
  }
  if (schema?.kind === 'multi') {
    return (schema.cells as any[]).map((c) => ({ cell: c.key, value: null, verdict: null }));
  }
  // TODO(Task 16): per_cylinder_subset reads parent_run.flagged_cells — requires
  // parentRunId to be passed in and the parent run loaded. Deferred until
  // DiagnosticsTab wires parent run context. Until then, compression_wet renders
  // with zero inputs and the "Save to WO" button stays disabled.
  return [];
}

/**
 * Minimal client-side mirror of ThresholdEvaluator. Only supports the simple
 * arithmetic + comparison + &&/|| subset used by the seeded threshold formulas.
 * Server-side evaluation is authoritative — this is for live cell feedback only.
 * Disallowed characters → return false (fail-safe).
 */
function evalExpr(
  expr: string,
  value: number,
  spec: { min?: number; max?: number }
): boolean {
  let e = expr;
  e = e.replace(/\bspec\.min\b/g, String(spec.min ?? 'NaN'));
  e = e.replace(/\bspec\.max\b/g, String(spec.max ?? 'NaN'));
  e = e.replace(/\bvalue\b/g, String(value));
  // Only permit digits, whitespace, basic arithmetic + comparison + logical ops, parens, and dots
  if (!/^[\s0-9+\-*/.<>=!&|()NaIfny]+$/.test(e)) return false;
  try {
    // eslint-disable-next-line no-new-func
    return Function(`"use strict";return (${e});`)() === true;
  } catch {
    return false;
  }
}

function cellVerdict(
  value: number,
  spec: any,
  perCell: { green: string; yellow: string; red: string }
): Verdict | null {
  try {
    if (evalExpr(perCell.red, value, spec)) return 'red';
    if (evalExpr(perCell.yellow, value, spec)) return 'yellow';
    if (evalExpr(perCell.green, value, spec)) return 'green';
  } catch {
    /* swallow */
  }
  return null;
}

export const DiagnosticForm: React.FC<DiagnosticFormProps> = ({ run, procedure, onCompleted }) => {
  const [cells, setCells] = useState<CellState[]>(() => buildCells(procedure, run));
  const [spec, setSpec] = useState<SpecUsed | null>(run.spec_used);
  const [loadingSpec, setLoadingSpec] = useState(false);
  const [saving, setSaving] = useState(false);

  // Computed overall verdict from cells in state.
  // worst-of: worst per-cell verdict + variance verdict (if procedure config has it).
  // This mirrors server-side VerdictComputer.overallVerdict so the live feedback
  // bar shows the same verdict the server will store on Save — no surprises at completion.
  const overallVerdict = useMemo<Verdict | null>(() => {
    const filled = cells.filter((c) => c.verdict);
    if (filled.length === 0) return null;

    // Per-cell worst
    let perCellWorst: Verdict | null = null;
    if (filled.some((c) => c.verdict === 'red')) perCellWorst = 'red';
    else if (filled.some((c) => c.verdict === 'yellow')) perCellWorst = 'yellow';
    else if (filled.length === cells.length && cells.every((c) => c.verdict === 'green'))
      perCellWorst = 'green';

    // Variance (only when the procedure config has a variance block AND we have ≥2 finite values)
    let variance: Verdict | null = null;
    const varianceCfg = procedure.threshold_logic?.variance;
    if (varianceCfg && filled.length >= 2) {
      const values = filled.map((c) => c.value as number).filter((v) => Number.isFinite(v));
      if (values.length >= 2) {
        const max = Math.max(...values);
        const min = Math.min(...values);
        if (max > 0) {
          const v = (max - min) / max;
          if (v >= varianceCfg.limit_red) variance = 'red';
          else if (v >= varianceCfg.limit_yellow) variance = 'yellow';
          else variance = 'green';
        }
      }
    }

    // worst-of
    const RANK: Record<Verdict, number> = { green: 0, yellow: 1, red: 2 };
    const ranks: number[] = [];
    if (perCellWorst) ranks.push(RANK[perCellWorst]);
    if (variance) ranks.push(RANK[variance]);
    if (ranks.length === 0) return null;
    const worst = Math.max(...ranks);
    return (['green', 'yellow', 'red'] as Verdict[])[worst];
  }, [cells, procedure.threshold_logic]);

  // Trigger spec cascade on mount if not already populated.
  useEffect(() => {
    if (!spec) {
      setLoadingSpec(true);
      diagnosticService
        .getSpec(run.id)
        .then(({ data }) => setSpec(data.spec))
        .finally(() => setLoadingSpec(false));
    }
  }, [run.id, spec]);

  const handleCellChange = (idx: number, val: number | null) => {
    setCells((prev) => {
      const next = [...prev];
      const v = val ?? null;
      const verdict =
        v != null && spec
          ? cellVerdict(
              v,
              { min: spec.min, max: spec.max },
              procedure.threshold_logic?.per_cell ?? { green: '', yellow: '', red: '' }
            )
          : null;
      next[idx] = { ...next[idx], value: v, verdict };
      return next;
    });
  };

  const buildCellResults = (): CellResult[] =>
    cells
      .filter((c) => c.value != null)
      .map((c) => ({
        cell: c.cell,
        value: c.value as number,
        verdict: (c.verdict as Verdict) ?? 'red',
      }));

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await diagnosticService.updateResults(run.id, { cells: buildCellResults() });
      message.success('saved');
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? e?.message ?? 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await diagnosticService.updateResults(run.id, { cells: buildCellResults() });
      const { data } = await diagnosticService.complete(run.id);
      onCompleted?.(data);
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? e?.message ?? 'complete failed');
    } finally {
      setSaving(false);
    }
  };

  const allFilled = cells.length > 0 && cells.every((c) => c.value != null);
  const layout = procedure.display_config?.layout ?? 'grid';
  const units =
    procedure.inputs_schema?.cell?.units ?? procedure.display_config?.units ?? '';

  return (
    <div className="diag-form">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card size="small">
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text strong>{procedure.name}</Text>
            {loadingSpec ? (
              <Text type="secondary">checking sources…</Text>
            ) : (
              <SpecBadge
                source={spec?.source}
                attribution={spec?.attribution_text}
                min={spec?.min}
                max={spec?.max}
                units={units}
              />
            )}
          </Space>
        </Card>

        <VerdictBar verdict={overallVerdict} />

        <Row gutter={[8, 8]}>
          {cells.map((c, i) => (
            <Col
              key={c.cell}
              xs={layout === 'grid' ? 12 : 24}
              sm={layout === 'grid' ? 8 : 12}
              md={layout === 'grid' ? 6 : 12}
            >
              <Card
                size="small"
                className={`diag-cell diag-cell--${c.verdict ?? 'empty'}`}
              >
                <div className="diag-cell-label">{c.cell.toUpperCase()}</div>
                <InputNumber
                  value={c.value as any}
                  onChange={(v) => handleCellChange(i, v as number | null)}
                  style={{ width: '100%' }}
                  controls={false}
                  min={procedure.inputs_schema?.cell?.min_input ?? 0}
                  max={procedure.inputs_schema?.cell?.max_input}
                  data-testid={`diag-cell-input-${c.cell}`}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Space>
          <Button onClick={handleSaveDraft} loading={saving}>
            Save draft
          </Button>
          <Button
            type="primary"
            disabled={!allFilled}
            loading={saving}
            onClick={handleComplete}
          >
            Save to WO
          </Button>
        </Space>
      </Space>
    </div>
  );
};
